<script setup>

import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";
import {usePage} from "@inertiajs/vue3";
import {onMounted, onBeforeUnmount, ref, computed} from "vue";

const componentProps = defineProps({
    hideTitle: {
        type: Boolean,
        default: false
    },
    buttonOrder: {
        type: Array,
        default: () => ['yearly', 'monthly', 'trial', 'paypal']
    },
    showGoldOptions: {
        type: Boolean,
        default: true
    },
    showAlternativePayments: {
        type: Boolean,
        default: true
    },
    totalSites: {
        type: Number,
        default: null
    },
    source: {
        type: String,
        default: ''
    }
});

const { props } = usePage()

// Blue membership options
const monthlyPrice = ref(0)
const monthlyUrl = ref('')
const monthlyCurrencySymbol = ref('$')
const monthlyStrikePrice = ref('39.95')

const yearlyPrice = ref(0)
const yearlyPriceMonthly = ref(0)
const yearlyUrl = ref('')
const yearlyFullPrice = ref(0)
const yearlyCurrencySymbol = ref('$')
const yearlyStrikePrice = ref('359.40')

const trialPrice = ref(0)
const trialUrl = ref('')
const trialCurrencySymbol = ref('$')

const paypalUrl = ref('')

// Gold membership options
const monthlyGoldPrice = ref(0)
const monthlyGoldPerDay = ref(0)
const monthlyGoldUrl = ref('')
const monthlyGoldCurrencySymbol = ref('$')

const yearlyGoldPrice = ref(0)
const yearlyGoldPerDay = ref(0)
const yearlyGoldUrl = ref('')
const yearlyGoldCurrencySymbol = ref('$')

// Alternative payment options
const bitcoinUrl = ref('')
const giftcardUrl = ref('')

const geoCode = ref('US')

onMounted(() => {
    
    clearJoinOptions();

    // Blue membership
    if (props.joinOptions?.button_monthly) {
    monthlyPrice.value = props.joinOptions.button_monthly.data?.formatted_initial_amount;
    monthlyUrl.value = props.joinOptions.button_monthly.url;
        monthlyCurrencySymbol.value = props.joinOptions.button_monthly.data?.currency_symbol || '$';
    }

    if (props.joinOptions?.button_yearly) {
    yearlyPrice.value = props.joinOptions.button_yearly.data?.formatted_initial_amount;
    yearlyPriceMonthly.value = props.joinOptions.button_yearly.data?.formatted_monthly_amount;
    yearlyUrl.value = props.joinOptions.button_yearly.url;
        yearlyFullPrice.value = props.joinOptions.button_yearly.data?.formatted_initial_amount;
        yearlyCurrencySymbol.value = props.joinOptions.button_yearly.data?.currency_symbol || '$';
    }

    if (props.joinOptions?.button_trial) {
    trialPrice.value = props.joinOptions.button_trial.data?.formatted_initial_amount;
    trialUrl.value = props.joinOptions.button_trial.url;
        trialCurrencySymbol.value = props.joinOptions.button_trial.data?.currency_symbol || '$';
    }

    if (props.joinOptions?.button_paypal) {
        paypalUrl.value = props.joinOptions.button_paypal.url;
    }

    // Gold membership
    if (props.joinOptions?.button_monthlyGold) {
        monthlyGoldPrice.value = props.joinOptions.button_monthlyGold.data?.formatted_initial_amount;
        monthlyGoldPerDay.value = props.joinOptions.button_monthlyGold.data?.formatted_per_day_amount;
        monthlyGoldUrl.value = props.joinOptions.button_monthlyGold.url;
        monthlyGoldCurrencySymbol.value = props.joinOptions.button_monthlyGold.data?.currency_symbol || '$';
    }

    if (props.joinOptions?.button_yearlyGold) {
        yearlyGoldPrice.value = props.joinOptions.button_yearlyGold.data?.formatted_initial_amount;
        yearlyGoldPerDay.value = props.joinOptions.button_yearlyGold.data?.formatted_per_day_amount;
        yearlyGoldUrl.value = props.joinOptions.button_yearlyGold.url;
        yearlyGoldCurrencySymbol.value = props.joinOptions.button_yearlyGold.data?.currency_symbol || '$';
    }

    // Alternative payments
    if (props.joinOptions?.button_bitcoin) {
        bitcoinUrl.value = props.joinOptions.button_bitcoin.url;
    }

    if (props.joinOptions?.button_giftcard) {
        giftcardUrl.value = props.joinOptions.button_giftcard.url;
    }

    // Get geo info
    if (props.geoip?.iso_code) {
        geoCode.value = props.geoip.iso_code;
    }
})


onBeforeUnmount(() => {
    // Trigger your cleanup/close logic here
    console.log('BlueJoinOptions modal closed');
    clearJoinOptions();
})

const handleSubscribeButtonClick = (option) => {
    const urlMap = {
        'monthly': monthlyUrl.value,
        'yearly': yearlyUrl.value,
        'trial': trialUrl.value,
        'paypal': paypalUrl.value,
        'monthlyGold': monthlyGoldUrl.value,
        'yearlyGold': yearlyGoldUrl.value,
        'bitcoin': bitcoinUrl.value,
        'giftcard': giftcardUrl.value
    }
    
    if (urlMap[option]) {

        console.log('++ BlueJoinOptions: From ' + componentProps.source + ' Redirecting to', urlMap[option]);
        
        var currentAuditionProps = window.auditionProps();
        currentAuditionProps.source = componentProps.source;
        currentAuditionProps.join_type = option;
        urlMap[option] = urlMap[option] + (urlMap[option].includes('?') ? '&custom8=' + componentProps.source : '?custom8=' + componentProps.source);
        // also save that to local storage under the key 'blue_join_options_source'
        localStorage.setItem('blue_join_options_source', componentProps.source);
        localStorage.setItem('blue_join_options_join_type', option);
        localStorage.setItem('blue_join_options_url', urlMap[option]);
        localStorage.setItem('blue_join_options_video_props', JSON.stringify(currentAuditionProps));
        
        console.log('++ BlueJoinOptions: Audition Props', currentAuditionProps);
        if(typeof LTIAnalytics !== 'undefined' && currentAuditionProps.is_auditions_video) {
            LTIAnalytics.track('Biller Join Button Clicked', currentAuditionProps);
        }
        window.location.href = urlMap[option]
    }
}

const shouldShowGiftcard = computed(() => {
    return geoCode.value === 'US' && componentProps.showAlternativePayments;
})

const totalSitesCount = computed(() => {
    // Priority: 1. Component prop, 2. Backend goldSites config, 3. Backend total_sites config
    if (componentProps.totalSites) {
        return componentProps.totalSites;
    }
    
    if (props.goldSites?.total_sites) {
        return props.goldSites.total_sites;
    }
    
    if (props.total_sites) {
        return props.total_sites;
    }
    
    // Fallback - should not normally reach here if backend is configured correctly
    return 11;
})
</script>

<template>
    <div class="join-options-container">
        <!-- Title and Features Section -->
        <h3 v-if="!componentProps.hideTitle" class="join-options-title">Become a Member Today!</h3>
        <h4 class="get-access">GET ACCESS TO <small>(EXCLUDES TRIAL)</small></h4>
        <div class="access-list-wrapper">
            <ul class="access-list">
                <li>Access to <strong>12k+</strong> Original Videos</li>
                <li>Daily Releases</li>
                
                <li>Access to 4k Videos</li>
                
                    <li><span class="stream-download-text">Download Videos</span>
                    </li>
               
                <li>Access to Entire VR Library</li>
                <li>Free $20 NA Live Credits</li>
                <li>Exclusive Partner Deals</li>
            </ul>
                </div>
                
        <!-- Blue Membership Options -->
        <div class="blue-join-options">
            <!-- Yearly Button -->
            <button 
                v-if="componentProps.buttonOrder.includes('yearly')"
                @click="handleSubscribeButtonClick('yearly')" 
                class="btn-join-standard yearly-deal">
                <div class="best-deal new-deal">
                    <img src="https://images.naughtycdn.com/nonsecure/images/best-deal-tag.png" alt="Best Deal" width="100%">
                </div>
                <div class="annual-text">
                    <p class="discount-badge">69% OFF</p>
                    <p class="yearly-title">ANNUAL</p>
                    <p class="small-text">Access to <strong>12k+</strong> Original Videos</p>
                </div>
                <div class="btn-right-container">
                    <p class="yearly_currency">
                        {{yearlyCurrencySymbol}}{{yearlyPriceMonthly}}<small>/mo</small>
                    </p>
                    <p class="yearly-billing-text">
                        *Billed at <span class="yearly_monthly_billing_span">{{yearlyStrikePrice}}</span>
                        {{yearlyCurrencySymbol}}{{yearlyPrice}}
                        </p>
                    </div>
            </button>

            <!-- Monthly Button -->
            <button 
                v-if="componentProps.buttonOrder.includes('monthly')"
                @click="handleSubscribeButtonClick('monthly')" 
                class="btn-join-standard">
                <div class="join-button-container">
                    <span>Monthly</span>
                    
                    <span class="small-text">Access to <strong>12k+</strong> Original Videos</span>
                            </div>
                <div class="btn-right-container">
                    <p class="monthly-currency">
                        {{monthlyCurrencySymbol}}{{monthlyPrice}}<small>/mo</small>
                    </p>
                    <span class="red-crossout red-crossout-price">{{monthlyStrikePrice}}</span>
                            </div>
            </button>

            <!-- Trial Button -->
            <button 
                v-if="componentProps.buttonOrder.includes('trial')"
                @click="handleSubscribeButtonClick('trial')" 
                class="btn-join-standard">
                <div class="trial_text">
                    <span>LIMITED 3-DAY TRIAL</span>
                        </div>
                <div class="btn-right-container">
                    <p class="trial_currency">
                        {{trialCurrencySymbol}}{{trialPrice}}
                    </p>
                </div>
            </button>

            <!-- PayPal Button -->
            <button 
                v-if="componentProps.buttonOrder.includes('paypal') && paypalUrl"
                @click="handleSubscribeButtonClick('paypal')" 
                class="btn-join-paypal">
                <div class="paypal-yearly-text">
                    <b>PAYPAL</b>
                    <span class="small-text">Access to <strong>12k+</strong> Original Videos</span>
                    </div>
                <div class="btn-right-container paypal-discount-container">
                    <p class="paypal-icon">
                        <img width="20" height="auto" src="https://images.naughtycdn.com/nonsecure/images/pp_icon.png" alt="PayPal Icon">
                    </p>
                            </div>
            </button>
                        </div>

       
        <!-- Alternative Payment Methods -->
        <div v-if="componentProps.showAlternativePayments && (bitcoinUrl || giftcardUrl)" class="alternative-payments">
            <h3 class="anon-payment-title">Pay Anonymously, Trade Unused Gift Cards</h3>
            <p class="small-text anon-payment-text">Access to <strong>12k+</strong> Original Videos</p>
            <div class="alternative-join-container">
                <!-- Giftcard (US only) -->
                <button 
                    v-if="shouldShowGiftcard && giftcardUrl"
                    @click="handleSubscribeButtonClick('giftcard')" 
                    class="small-join-link">
                    <p class="small-join-text">GIFTCARD</p>
                    <div class="right-side-icon">
                        <img class="giftcard-icon" width="24" height="auto" src="https://images.naughtycdn.com/nonsecure/images/giftcard_icon.png" alt="Gift card">
                    </div>
                </button>

                <!-- Crypto -->
                <button 
                    v-if="bitcoinUrl"
                    @click="handleSubscribeButtonClick('bitcoin')" 
                    class="small-join-link">
                    <p class="small-join-text">CRYPTO</p>
                    <div class="right-side-icon">
                        <img class="crypto-icon" width="18" height="auto" src="https://images.naughtycdn.com/nonsecure/images/icon-bitcoin.png" alt="Bitcoin symbol">
                    </div>
                </button>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* ============================================================================
 * LAYOUT & CONTAINERS
 * ============================================================================ */

.join-options-container {
    margin-bottom: 20px;
    max-width: 325px;
    margin-left: 30px;
    width: 100%;

}

.access-list-wrapper {
    display: flex;
    gap: 10px;
    justify-content: left;
    margin-bottom: 10px;
}

.alternative-join-container {
    display: flex;
    flex-direction: row;
    gap: 10px;
    padding-top: 10px;
    justify-content: center;
    padding-bottom: 20px;
}

.blue-join-options {
    display: flex;
    flex-direction: column;
    align-items: left;
    gap: 0;
}

.gold-section {
    margin-top: 20px;
}

/* ============================================================================
 * HEADINGS & TYPOGRAPHY
 * ============================================================================ */

.join-options-container h3.join-options-title {
    text-align: left;
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    color: #fff;
    line-height: normal;
    margin-bottom: 10px;
}

.join-options-container h4.get-access {
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: 29px;
    text-align: left;
    text-transform: uppercase;
    color: #fff;
    margin-bottom: 0px;
    margin-top: 10px;
}

.join-options-container h4 small {
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 29px;
    text-transform: uppercase;
}

.streaming-text {
    color: #FFF;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    letter-spacing: -0.16px;
    margin-bottom: 12px;
    text-align: center;
}

/* ============================================================================
 * STANDARDIZED BUTTON STYLES
 * ============================================================================ */

.btn-join-standard {
    width: 100%;
    max-width: 325px;
    height: 70px;
    border-radius: 4px;
    background: #06429D;
    position: relative;
    display: block;
    overflow: visible;
    margin-bottom: 16px;
    border: none;
    cursor: pointer;
}

.btn-join-standard.yearly-deal .btn-right-container {
    background: #0057D8;
}

.btn-join-standard:hover {
    background: #0057D8;
}

.btn-join-standard.yearly-deal {
    background: #0064FA;
    height: 100px;
}

.btn-join-standard.yearly-deal:hover {
    background: #0057D8;
}

.btn-join-paypal {
    width: 325px;
    height: 45px;
    border-radius: 4px;
    background: #06429D;
    position: relative;
    display: block;
    overflow: hidden;
    margin-bottom: 16px;
    border: none;
    cursor: pointer;
}

.btn-join-paypal:hover {
    background: #0057D8;
}

.btn-right-container {
    border-radius: 0 4px 4px 0;
    background: #212F50;
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    width: 130px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 0;
    border-left: 1px solid #082551;
}

.btn-join-standard.yearly-deal .btn-right-container {
    background: #0057D8;
}

/* ============================================================================
 * SMALL JOIN BUTTONS (Alternative Payment Methods)
 * ============================================================================ */

.small-join-link {
    background-color: #06429D !important;
    width: 165px;
    height: 45px;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    border: none;
    cursor: pointer;
}

.small-join-link:hover {
    background-color: #0057D8 !important;
}

.small-join-text {
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    text-transform: uppercase;
    color: #fff;
    width: 60%;
    margin: 0;
}

.right-side-icon {
    width: 40%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0 4px 4px 0;
    background: #212F50;
}

.crypto-icon {
    width: 18px;
    height: auto;
}

.giftcard-icon {
    width: 24px;
    height: auto;
}

.anon-payment-text {
    padding: 5px 0 2px;
    text-align: center;
}

/* ============================================================================
 * BUTTON CONTENT CONTAINERS
 * ============================================================================ */

.join-button-container {
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    text-transform: uppercase;
    color: #fff;
    position: absolute;
    top: 0px;
    left: 0px;
    width: 60%;
    height: 100%;
    z-index: 10;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 5px;
    flex-direction: column;
    gap: 3px;
}

.join-button-container span {
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    text-transform: uppercase;
    color: #fff;
}

.small-billing-note {
    font-size: 8px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    position: absolute;
    bottom: 12px;
    width: 125px;
    right: 0;
    color: #fff;
}

.annual-text {
    width: 60%;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: 3px;
    height: 100px;
}

/* ============================================================================
 * BADGES & LABELS
 * ============================================================================ */

.discount-badge {
    color: #FFFC00;
    text-align: center;
    font-size: 16px;
    font-style: normal;
    font-weight: 800;
    line-height: 16px;
    text-transform: uppercase;
    margin: 0;
}

.best-deal {
    position: absolute;
    top: -4px;
    left: -15px;
    z-index: 100;
    width: 70px;
}

.best-deal.new-deal {
    position: absolute;
    top: -4px;
    left: -15px;
    width: 70px;
    height: auto;
    z-index: 100;
}

.gold-only-badge {
    display: inline-block;
    background-color: #FFC107;
    text-align: center;
    margin-left: 5px;
    text-transform: none;
    vertical-align: middle;
    border-radius: 5px;
    background: #EAB308;
    width: 50px;
    line-height: 13px;
    height: 13px;
    flex-shrink: 0;
    color: #000;
    font-size: 8px;
    font-style: normal;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
}

/* ============================================================================
 * TEXT STYLES
 * ============================================================================ */

.small-text {
    color: #FFF;
    font-size: 11px;
    font-style: normal;
    font-weight: 500;
    line-height: 11px;
    padding: 0 4px;
}

.small-text strong {
    font-size: 11px;
    font-weight: 700;
    color: #fff;
}

.join-button-container span.small-text,
span.small-text {
    font-size: 11px !important;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    color: #fff;
    line-height: 11px;
    display: inline-block;
}


.stream-download-text {
    white-space: nowrap;
    color: #fff;
}

/* ============================================================================
 * TRIAL BUTTON STYLES
 * ============================================================================ */

.trial_text {
    font-size: 16px;
    font-style: normal;
    font-weight: 300;
    line-height: normal;
    text-transform: uppercase;
    text-align: center;
    width: 60%;
    position: absolute;
    top: 0 !important;
    left: 0px;
    font-weight: normal;
    line-height: 70px !important;
    margin: 0;
    height: 70px;
    display: flex;
    position: absolute;
    justify-content: center;
    align-items: center;
}

.trial_text span {
    display: block;
    color: #fff;
    font-size: 16px;
    font-style: normal;
    font-weight: 300;
    line-height: 17px;
    text-transform: uppercase;
    text-align: center;
}

.trial_currency {
    width: 100%;
    text-align: center;
    position: relative;
    top: 0;
    left: 0;
    font-size: 26px;
    font-style: normal;
    font-weight: 700;
    line-height: 70px;
    color: #fff;
    margin: 0;
}

/* ============================================================================
 * YEARLY BUTTON STYLES
 * ============================================================================ */

.yearly-title {
    font-size: 18px !important;
    font-style: normal;
    font-weight: 500;
    line-height: 18px;
    text-transform: uppercase;
    color: #fff;
    display: block;
    margin: 0;
}

.yearly-billing-text {
    color: #FFF;
    font-size: 8px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    display: block;
    text-align: center;
    margin: 0;
}

.yearly_monthly_billing_span {
    text-decoration: none;
    position: relative;
    color: #fff;
    font-weight: 700;
}

.yearly_monthly_billing_span::before {
    content: '';
    position: absolute;
    width: 110%;
    height: 1px;
    background-color: red;
    left: -5%;
    top: 50%;
    transform: rotate(15deg);
}

/* ============================================================================
 * CURRENCY & PRICING DISPLAY
 * ============================================================================ */

.btn-right-container .yearly_currency,
.btn-right-container .monthly-currency {
    position: relative;
    top: 0;
    left: 0;
    width: 100%;
    text-align: center;
    line-height: 25px;
    margin: 0;
}

.btn-right-container .yearly_currency {
    font-size: 26px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    text-align: center;
    padding-right: 0px;
    color: #fff;
}

.yearly_currency small,
.monthly-currency small {
    font-size: 7px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    text-transform: lowercase;
}

.monthly-currency {
    font-size: 26px;
    font-style: normal;
    font-weight: 800;
    line-height: normal;
    text-transform: uppercase;
    color: #fff;
    line-height: 25px;
    z-index: 100;
    padding: 0;
    margin: 0;
}

/* ============================================================================
 * PRICE STRIKETHROUGH STYLES
 * ============================================================================ */

.red-crossout-price {
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    position: relative;
    color: #fff;
    display: block;
    text-align: center;
    padding-top: 0px;
    top: 1px;
}

.red-crossout::before,
.red-crossout-price::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 2px;
    background-color: red;
    left: 0;
    top: 50%;
    transform: rotate(-10deg);
}

/* ============================================================================
 * PAYPAL BUTTON STYLES
 * ============================================================================ */

.paypal-yearly-text {
    z-index: 50;
    color: #fff;
    line-height: 16px;
    font-size: 18px;
    font-weight: 800;
    text-align: center;
    width: 60%;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 3px;
}

.paypal-yearly-text b {
    color: #FFF;
    text-align: center;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
}

.paypal-yearly-text .small-text {
    font-size: 10px !important;
}

.paypal-icon {
    position: relative;
    width: 100%;
    z-index: 10;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    padding: 0;
}

.paypal-icon img {
    width: 20px;
    height: auto;
}

/* ============================================================================
 * GOLD MEMBERSHIP STYLES
 * ============================================================================ */

.gold-unlimited-title {
    color: #FFF;
    text-align: center;
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: 0.18px;
    text-transform: uppercase;
    margin-bottom: 10px;
}

.gold-unlimited-title span {
    margin-right: 6px;
    position: relative;
    top: 2px;
    display: inline-block;
}
.gold-section {
    justify-content: left;
    display: flex;
    flex-direction: column;
    max-width: 325px;
}

.gold-button-text {
    color: #000;
    width: 60%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 57px;
    position: absolute;
    left: 0;
    top: 0;
}

.gold-button-text span {
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    text-transform: uppercase;
    display: block;
    color: #000;
}

.gold-button-text span:last-child {
    font-size: 26px;
    font-style: normal;
    font-weight: 700;
    line-height: 26px;
    text-transform: uppercase;
    display: block;
}

.gold-unlimited-text {
    color: #FFF;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    letter-spacing: -0.14px;
    text-align: center;
    width: 100%;
    margin-bottom: 10px;
}

.gold-unlimited-text b {
    font-weight: 800;
    color: #fff;
}

/* Gold button base styles */
.gold-btn,
.gold-btn p,
.gold-btn b,
.gold-btn span {
    color: #000 !important;
}

.join-link.payment-method.gold-btn {
    background: linear-gradient(90deg, #F59E0B 0%, #EAB308 100%);
    height: 100px;
    width: 325px;
    margin: 0 auto 16px;
    border-radius: 4px;
    position: relative;
    overflow: visible;
    border: none;
    cursor: pointer;
}

.join-link.payment-method.gold-btn:hover {
    background: linear-gradient(90deg, #EAB308 0%, #F59E0B 100%);
}

.gold-yearly-btn {
    margin-bottom: 25px !important;
}

/* Gold monthly currency */
.gold-monthly-currency {
    color: #fff;
    z-index: 50;
    color: #f4f6fa;
    line-height: 0.8em;
    font-size: 26px;
    text-align: center;
    position: absolute;
    top: 0;
    line-height: 100px;
    font-weight: 800;
    background-color: #cb8308;
    height: 100px;
    width: 130px;
    border-left: 1px solid #845607;
    display: block;
    right: 0;
    border-radius: 0 4px 4px 0;
    margin: 0;
}

/* Gold yearly currency */
.gold-yearly_currency {
    color: #f4f6fa !important;
    z-index: 50;
    font-size: 26px;
    text-align: center;
    position: absolute;
    top: 0;
    line-height: 100px;
    width: 130px;
    font-weight: 800;
    border-left: 1px solid #845607;
    display: block;
    background-color: #cb8308;
    right: 0;
    border-radius: 0 4px 4px 0;
    height: 100px;
    margin: 0;
}

/* Gold yearly per month */
.gold-yearly_per_month {
    z-index: 50;
    color: #f4f6fa;
    line-height: 0.8em;
    font-size: 10px;
    text-align: left;
    width: 93px;
    position: absolute;
    top: 60px;
    left: 60px;
    font-weight: 700;
}

/* ============================================================================
 * SITES LINK & ACTION BUTTONS
 * ============================================================================ */

.sites-action {
    position: absolute;
    bottom: 10px;
    left: 15px;
}

.sites-link {
    display: flex;
    align-items: center;
    background-color: #E8BA48;
    padding: 4px 15px;
    border-radius: 6px;
    text-decoration: none;
    color: #000;
    box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.25);
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
}

.sites-link:hover {
    background-color: #f0c050;
    text-decoration: none;
    color: #000;
}

.star-icon {
    margin-right: 6px;
    display: flex;
    align-items: center;
}

/* ============================================================================
 * ACCESS LIST STYLES
 * ============================================================================ */

.access-list {
    font-size: 10px;
    font-style: normal;
    font-weight: 500;
    line-height: 29px;
    text-transform: uppercase;
    color: #fff;
    list-style: disc;
    text-align: left;
    margin-left: 12px;
    padding-right: 0px;
    margin-bottom: 10px;
    columns: 2;
    column-gap: 20px;
}

.access-list li {
    font-size: 10px;
    margin-bottom: 0;
    color: #fff;
    min-height: 10px;
    height: auto;
    line-height: 20px;
    padding-right: 0px;
    text-transform: none;
    break-inside: avoid;
}

.access-list li strong {
    font-weight: bold;
    color: #fff;
}

.anon-payment-title {
    margin-top: 50px;
    color: #fff;
    font-size: 18px;
    font-weight: 600;
    text-align: center;
}

/* ============================================================================
 * MEDIA QUERIES
 * ============================================================================ */

@media only screen and (max-width: 1440px) {
    .btn-join-standard,
    .btn-join-paypal,
    .join-link.payment-method.gold-btn {
        width: 100%;
        max-width: 100%;
    }

    .paypal-yearly-text {
        width: 62% !important;
    }

    .alternative-join-container {
        flex-direction: column;
    }

    .small-join-link {
        width: 100%;
    }

    .join-button-container {
        width: 62%;
    }

    .trial_text {
        width: 62%;
        display: flex;
        height: 70px;
        line-height: 20px !important;
        justify-content: center;
        align-items: center;
    }

    .btn-right-container {
        width: 40%;
    }

    .btn-right-container .yearly_currency {
        font-size: 22px;
        padding-right: 10px;
    }

   

   

   
}

@media (max-width: 1340px) {
    .btn-right-container .yearly_currency {
        font-size: 22px !important;
    }

    .btn-join-standard.yearly-deal {
        height: 100px !important;
    }

    .annual-text {
        height: 100px;
    }
}

@media (max-width: 1220px) {
    .btn-right-container .yearly_currency,
    .yearly_currency,
    .monthly-currency,
    .trial_currency {
        font-size: 18px !important;
        padding-right: 0px !important;
    }
}

@media (max-width: 1100px) {
    .join-options-container h3.join-options-title {
        font-size: 18px;
    }

    .gold-unlimited-title {
        font-size: 14px;
    }

    .join-options-container h4.get-access {
        font-size: 14px;
    }

    .join-options-container h4 small {
        font-size: 10px;
    }

    .access-list-wrapper {
        flex-direction: column;
    }

    
}
@media (max-width: 639px) {
    .join-options-container,.gold-section {
        margin-left: 30px;
    }
    .access-list {
        columns: 1;
    }
}
@media (max-width: 440px) {
    .join-options-container,.gold-section {
        margin-left: 0px;
    }
    
}

</style>
